import { useAuthStore } from '@/stores/authStore';
import { num } from '@/lib/format';
import { db } from '@/lib/db';
import { newId } from '@/lib/ids';
import { queue } from '@/lib/sync';
import type { ConcreteCustomer, ConcreteDelivery, ConcreteDisposalReason } from '@/types/models';

/**
 * Who may do what in the concrete module. Mirrors the 0035 helpers
 * (app.concrete_view / _manage / _lead / _list_editor): the server
 * enforces, this only decides what the screens offer.
 *
 *   quarry manager / admin   every branch they may see (0055), everything
 *   branch manager           own branch(es): capture, correct, targets, lists
 *   branch supervisor        own branch: capture, correct any delivery
 *   branch operator          own branch: capture, correct own deliveries
 */
export interface ConcreteAccess {
  /** Any concrete at all. */
  canUse: boolean;
  /** A branch user (sees only their branch, none of the quarry). */
  isBranch: boolean;
  branchId: string | null;
  canView: (site: string | null | undefined) => boolean;
  canManage: (site: string | null | undefined) => boolean;
  canLead: (site: string | null | undefined) => boolean;
  canEditLists: boolean;
  userId: string | null;
}

export function useConcreteAccess(): ConcreteAccess {
  const profile = useAuthStore((s) => s.profile);
  const access = useAuthStore((s) => s.access);
  // Subscribed so a branch switch re-renders every concrete screen.
  useAuthStore((s) => s.selectedBranch);
  const branchId = useAuthStore.getState().branchId();
  const members = useAuthStore.getState().memberBranches();
  const hasModule = useAuthStore.getState().hasModule('concrete');
  const role = profile?.is_active === false ? null : profile?.role ?? null;
  const isBranch = !!profile?.home_site_id;
  // 0055 mirrors: quarry management within the sites it may see; a branch's
  // own people (explicitly assigned, possibly to several branches).
  const visible = (site: string | null | undefined) =>
    !!site && (!access || access.all_sites || access.sites.includes(site));
  const quarryLead = !isBranch && (role === 'manager' || role === 'admin');
  const own = (site: string | null | undefined) => !!site && members.includes(site);
  const branchRole = members.length > 0 ? role : null;

  return {
    canUse: hasModule && (quarryLead || members.length > 0),
    isBranch,
    branchId,
    canView: (site) => (quarryLead && visible(site)) || own(site),
    canManage: (site) => (quarryLead && visible(site))
      || (own(site) && (branchRole === 'supervisor' || branchRole === 'manager')),
    canLead: (site) => (quarryLead && visible(site)) || (own(site) && branchRole === 'manager'),
    canEditLists: quarryLead || branchRole === 'manager',
    userId: profile?.id ?? null,
  };
}

/** m³ with one decimal, through the app's one formatter. */
export const m3 = (v: number | string | null | undefined, decimals = 1) => num(v, decimals);

/* ------------------------------------------------------------------ */
/* Capture. Deliveries are field capture: they go to the local mirror  */
/* and the outbox, so a branch keeps working when the signal drops.    */
/* A customer added on the spot goes the same way, and ahead of the    */
/* delivery that needs it — the outbox sends in order.                 */
/* ------------------------------------------------------------------ */

export interface DeliveryDraft {
  id?: string;
  site_id: string;
  delivery_date: string;
  mix_id: string | null;
  customer_id: string | null;
  volume_m3: number | null;
  disposed_m3: number | null;
  disposal_reason_id: string | null;
  disposal_note: string | null;
  order_no: string | null;
  batch_no: string | null;
  truck_no: string | null;
  notes: string | null;
}

/** The same rules as the table and its trigger, so a mistake is caught at
 *  the plant rather than parked in Sync Issues later. */
export function validateDelivery(
  d: DeliveryDraft,
  reasons: ConcreteDisposalReason[],
  today: string,
): Record<string, string> {
  const e: Record<string, string> = {};
  if (!d.delivery_date) e.delivery_date = 'Date is required';
  else if (d.delivery_date > today) e.delivery_date = 'A delivery cannot be dated in the future';
  if (!d.mix_id) e.mix_id = 'Choose the mix';
  if (!d.customer_id) e.customer_id = 'Choose the customer';
  const vol = Number(d.volume_m3 ?? 0);
  if (!(vol > 0)) e.volume_m3 = 'Enter the m³ delivered';
  else if (vol > 60) e.volume_m3 = 'More than 60 m³ on one delivery — check the figure';
  const disp = Number(d.disposed_m3 ?? 0);
  if (disp < 0) e.disposed_m3 = 'Cannot be negative';
  else if (disp > vol) e.disposed_m3 = 'Cannot be more than was delivered';
  if (disp > 0) {
    if (!d.disposal_reason_id) e.disposal_reason_id = 'Say why it was disposed of or rejected';
    const r = reasons.find((x) => x.id === d.disposal_reason_id);
    if (r?.requires_note && !(d.disposal_note ?? '').trim()) e.disposal_note = 'This reason needs a note';
  }
  return e;
}

export async function saveDelivery(
  d: DeliveryDraft, existing: ConcreteDelivery | undefined, userId: string | null,
): Promise<ConcreteDelivery> {
  const disposed = Number(d.disposed_m3 ?? 0);
  const row: ConcreteDelivery = {
    ...(existing ?? {}),
    id: d.id ?? existing?.id ?? newId(),
    site_id: d.site_id,
    delivery_date: d.delivery_date,
    mix_id: d.mix_id!,
    customer_id: d.customer_id!,
    volume_m3: Number(d.volume_m3),
    disposed_m3: disposed,
    disposal_reason_id: disposed > 0 ? d.disposal_reason_id : null,
    disposal_note: disposed > 0 ? (d.disposal_note?.trim() || null) : null,
    order_no: d.order_no?.trim() || null,
    batch_no: d.batch_no?.trim() || null,
    truck_no: d.truck_no?.trim().toUpperCase() || null,
    notes: d.notes?.trim() || null,
    updated_at: new Date().toISOString(),
    // Kept locally so the screen knows whose it is before the server says.
    created_by: existing?.created_by ?? userId,
    created_at: existing?.created_at ?? new Date().toISOString(),
  } as ConcreteDelivery;
  await db.concrete_deliveries.put(row);
  // Only the columns the table has; the server fills the rest.
  const payload: Record<string, unknown> = { ...row };
  delete payload.updated_at;
  delete payload.created_at;
  delete payload.created_by;
  delete payload.updated_by;
  delete payload.deleted_at;
  await queue({ entity: 'concrete_delivery', entity_id: row.id, op: 'upsert', payload });
  return row;
}

export async function removeDelivery(id: string): Promise<void> {
  await db.concrete_deliveries.delete(id);
  await queue({ entity: 'concrete_delivery', entity_id: id, op: 'delete', payload: null });
}

export async function addCustomer(name: string, site: string | null): Promise<ConcreteCustomer> {
  const clean = name.trim().replace(/\s+/g, ' ');
  const existing = (await db.concrete_customers.toArray())
    .find((c) => c.name.trim().toLowerCase() === clean.toLowerCase());
  if (existing) return existing;
  const row = {
    id: newId(), name: clean, code: null, contact_name: null, phone: null, email: null,
    site_id: site, notes: null, is_active: true, updated_at: new Date().toISOString(),
  } as ConcreteCustomer;
  await db.concrete_customers.put(row);
  const payload: Record<string, unknown> = { ...row };
  delete payload.updated_at;
  await queue({ entity: 'concrete_customer', entity_id: row.id, op: 'upsert', payload });
  return row;
}
